import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, X, HelpCircle } from 'lucide-react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { PremiumFooter } from '@/components/premium/PremiumFooter';
import { SEO, FAQStructuredData } from '@/components/SEO';
import { RelatedPages } from '@/components/RelatedPages';

const COMPARISONS = [
  {
    title: 'Modular Workstations vs Traditional Carpentry',
    desc: 'The definitive comparison for Indian offices',
    rows: [
      { feature: 'Installation Time', modular: '2-4 hours', traditional: '2-4 weeks', winner: 'modular' },
      { feature: 'Cost (5-year TCO)', modular: '30-40% lower', traditional: 'Higher (maintenance)', winner: 'modular' },
      { feature: 'Relocatable', modular: 'Yes — disassemble & reassemble', traditional: 'No — destroyed on removal', winner: 'modular' },
      { feature: 'Customization', modular: 'Configurable modules', traditional: 'Fully custom on-site', winner: 'traditional' },
      { feature: 'Quality Consistency', modular: 'Factory-precision (CNC)', traditional: 'Varies by carpenter skill', winner: 'modular' },
      { feature: 'Dust / Disruption', modular: 'Zero (pre-built)', traditional: 'High (on-site sawing)', winner: 'modular' },
      { feature: 'Warranty', modular: 'Manufacturer warranty', traditional: 'None typically', winner: 'modular' },
      { feature: 'Scalability', modular: 'Add modules anytime', traditional: 'Full rebuild needed', winner: 'modular' },
    ],
  },
  {
    title: 'Privacy Booths vs Conference Rooms',
    desc: 'Space efficiency and acoustic comparison',
    rows: [
      { feature: 'Floor Space Needed', modular: '1-4 sqm per pod', traditional: '15-25 sqm per room', winner: 'modular' },
      { feature: 'Acoustic Isolation', modular: '30-40 dB reduction', traditional: '40-50 dB (if built well)', winner: 'traditional' },
      { feature: 'Setup Time', modular: 'Same day', traditional: '4-8 weeks construction', winner: 'modular' },
      { feature: 'Lease Flexibility', modular: 'Relocatable asset', traditional: 'Permanent fixture', winner: 'modular' },
      { feature: 'Cost per Seat', modular: '₹35,000-₹70,000', traditional: '₹1,50,000-₹3,00,000', winner: 'modular' },
      { feature: 'Ventilation & Power', modular: 'Built-in (LED, fan, USB)', traditional: 'Requires HVAC ducting', winner: 'modular' },
      { feature: 'Booking/Availability', modular: 'Walk-in (no booking)', traditional: 'Often overbooked', winner: 'modular' },
    ],
  },
  {
    title: 'MFS vs Imported Flat-Pack (IKEA-style)',
    desc: 'Factory-direct Indian vs imported furniture',
    rows: [
      { feature: 'Moisture Resistance', modular: 'Engineered for Indian humidity', traditional: 'Standard (warps in monsoon)', winner: 'modular' },
      { feature: 'Load Bearing', modular: 'Heavy-duty commercial grade', traditional: 'Home/light office grade', winner: 'modular' },
      { feature: 'Assembly', modular: 'Tool-free (Kholo Jodo Lagalo)', traditional: 'Allen key assembly', winner: 'modular' },
      { feature: 'After-Sales Support', modular: 'Direct from manufacturer', traditional: 'Limited (returns only)', winner: 'modular' },
      { feature: 'Bulk Discounts', modular: 'Yes (15-25% off)', traditional: 'No', winner: 'modular' },
      { feature: 'Delivery (Mumbai)', modular: 'Free + Installation', traditional: 'Paid, self-assembly', winner: 'modular' },
      { feature: 'Design Aesthetic', modular: 'Corporate/premium', traditional: 'Scandinavian/home', winner: 'tie' },
      { feature: 'Price Range', modular: '₹5,320 — ₹5,00,000', traditional: '₹3,000 — ₹50,000', winner: 'tie' },
    ],
  },
];

const FAQS = [
  { question: 'Is modular furniture better than traditional carpentry for offices?', answer: 'For most offices, yes. Modular furniture offers faster installation (hours vs weeks), zero dust/disruption, manufacturer warranty, and the ability to relocate or reconfigure. Traditional carpentry offers unlimited customization but at higher long-term cost and zero portability.' },
  { question: 'How do privacy booths compare to building a conference room?', answer: 'Privacy booths use 5-10x less floor space, cost 60-75% less per seat, and can be installed the same day. Conference rooms offer better acoustic isolation but require construction, HVAC, and are permanent fixtures that reduce lease flexibility.' },
  { question: 'Should I buy MFS or IKEA for my office?', answer: 'MFS is purpose-built for Indian commercial environments — moisture-sealed for monsoons, heavy-duty for daily office use, and includes free installation in Mumbai. IKEA is better suited for home offices or light-use scenarios. MFS also offers bulk discounts and direct manufacturer support.' },
  { question: 'Can modular furniture match the look of custom woodwork?', answer: 'Modern modular furniture uses premium laminates and edge-banding that closely replicate wood, stone, and fabric textures. While it may not perfectly match hand-carved artisanal woodwork, for 95% of corporate applications the visual difference is negligible while durability is significantly higher.' },
];

function ComparisonTable({ comparison }) {
  return (
    <section className="mb-14">
      <h2 className="text-xl font-extrabold text-[#1a1a1a] mb-1">{comparison.title}</h2>
      <p className="text-gray-500 text-sm mb-6">{comparison.desc}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm border border-gray-100">
          <thead>
            <tr className="bg-[#F8F6F1]">
              <th className="px-4 py-3 font-bold text-[#1a1a1a] text-xs uppercase tracking-wider w-1/3">Feature</th>
              <th className="px-4 py-3 font-bold text-[#c5a059] text-xs uppercase tracking-wider w-1/3">MFS / Modular</th>
              <th className="px-4 py-3 font-bold text-gray-500 text-xs uppercase tracking-wider w-1/3">Alternative</th>
            </tr>
          </thead>
          <tbody>
            {comparison.rows.map((row, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-4 py-3 text-gray-700 font-medium">{row.feature}</td>
                <td className={`px-4 py-3 ${row.winner === 'modular' ? 'text-[#2E7D32] font-semibold' : 'text-gray-700'}`}>
                  {row.winner === 'modular' && <Check className="w-3.5 h-3.5 inline mr-1 text-[#2E7D32]" />}
                  {row.modular}
                </td>
                <td className={`px-4 py-3 ${row.winner === 'traditional' ? 'text-[#2E7D32] font-semibold' : 'text-gray-500'}`}>
                  {row.winner === 'traditional' && <Check className="w-3.5 h-3.5 inline mr-1 text-[#2E7D32]" />}
                  {row.traditional}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function ComparisonGuide() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <SEO
        title="Modular vs Traditional Furniture — Comparison Guide | MFS"
        description="Compare modular office furniture with traditional carpentry, conference rooms, and imported flat-pack options. See pricing, features, and pros/cons for Indian offices."
        canonical="/comparison-guide"
        keywords="modular vs traditional furniture, privacy booth vs conference room, MFS vs IKEA, modular furniture comparison india"
      />
      <FAQStructuredData faqs={FAQS} />
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-16 max-w-5xl">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1a1a1a] uppercase tracking-tight mb-2">
          Office Furniture Comparison Guide
        </h1>
        <p className="text-gray-500 text-sm mb-12 max-w-2xl">
          An honest, side-by-side comparison to help you make the right decision for your office. Data based on 5,000+ office installations by Ezeefit since 2014.
        </p>

        {COMPARISONS.map((comp, idx) => (
          <ComparisonTable key={idx} comparison={comp} />
        ))}

        {/* CTA */}
        <div className="bg-[#1a1a1a] p-8 sm:p-10 text-center mb-14">
          <p className="text-white text-lg font-bold mb-2">Ready to Switch to Modular?</p>
          <p className="text-gray-400 text-sm mb-6">Get a free consultation with our workspace planning team.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/products">
              <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-white rounded-none px-8 py-4 text-xs tracking-[0.15em] font-bold">
                EXPLORE PRODUCTS <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/bulk-inquiry">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] rounded-none px-8 py-4 text-xs tracking-[0.15em] font-bold bg-transparent">
                GET FREE CONSULTATION
              </Button>
            </Link>
          </div>
        </div>

        {/* FAQ */}
        <section>
          <h2 className="text-xl font-extrabold text-[#1a1a1a] mb-6 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#c5a059]" /> Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <details key={i} className="group border border-gray-100 open:border-[#c5a059] transition-colors">
                <summary className="flex justify-between items-center cursor-pointer px-5 py-4 text-sm font-semibold text-[#1a1a1a] hover:text-[#c5a059]">
                  {faq.question}
                  <span className="text-[#c5a059] group-open:rotate-45 transition-transform text-lg">+</span>
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Interlinks */}
        <RelatedPages pageKey="comparisonGuide" className="mt-12" />
      </div>

      <PremiumFooter />
    </div>
  );
}
